<template>
  <div :key="locale" class="pixso-page police-solution" :class="{ 'police-solution--en': isEnglish }">
    <section class="police-solution__hero pixso-bleed" aria-labelledby="public-security-hero-title">
      <video
        class="police-solution__hero-video"
        autoplay
        muted
        loop
        playsinline
        aria-hidden="true"
      >
        <source :src="heroVideo" type="video/mp4" />
      </video>
      <div class="police-solution__hero-copy">
        <h1 id="public-security-hero-title" class="police-solution__hero-title">
          {{ t('PublicSecurityInspection.hero.title') }}
        </h1>
        <p class="police-solution__hero-subtitle">{{ t('PublicSecurityInspection.hero.subtitle') }}</p>
      </div>
    </section>

    <section class="police-solution__section police-solution__pain">
      <div class="pixso-container police-solution__section-head">
        <p class="pixso-eyebrow">{{ t('PublicSecurityInspection.sections.pain.eyebrow') }}</p>
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="police-solution__title-accent pixso-heading-xl">{{ t('PublicSecurityInspection.sections.pain.titleAccent') }}</span>
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.pain.title') }}</span>
        </h2>
      </div>

      <div class="pixso-container police-solution__pain-shell">
        <AccordionImageGallery
          :items="painGalleryItems"
          :aria-label="t('PublicSecurityInspection.sections.pain.eyebrow')"
        />
      </div>
    </section>

    <section class="police-solution__section police-solution__how pixso-bleed">
      <div class="pixso-stage police-solution__how-stage">
        <div class="pixso-container police-solution__how-copy">
          <h2 class="police-solution__how-title pixso-heading-xl">{{ t('PublicSecurityInspection.sections.how.title') }}</h2>
          <p class="police-solution__how-subtitle">{{ t('PublicSecurityInspection.sections.how.subtitle') }}</p>
          <p class="police-solution__how-english">{{ t('PublicSecurityInspection.sections.how.english') }}</p>
          <p class="police-solution__how-tags">{{ t('PublicSecurityInspection.sections.how.tags') }}</p>
        </div>
      </div>
    </section>

    <section class="police-solution__section police-solution__features-section">
      <div class="pixso-container police-solution__section-head">
        <p class="pixso-eyebrow">{{ t('PublicSecurityInspection.sections.features.eyebrow') }}</p>
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="police-solution__title-accent pixso-heading-xl">{{ t('PublicSecurityInspection.sections.features.titleAccent') }}</span>
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.features.title') }}</span>
        </h2>
      </div>

      <FeatureMosaicGrid
        class="police-solution__wide police-solution__feature-showcase"
        :items="featureMosaicItems"
        :mobile-split-index="5"
        :aria-label="t('PublicSecurityInspection.sections.features.title')"
      />
    </section>

    <section class="police-solution__section police-solution__hardware-section">
      <div class="pixso-container police-solution__section-head police-solution__section-head--hardware">
        <p class="pixso-eyebrow">{{ t('PublicSecurityInspection.sections.hardware.eyebrow') }}</p>
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.hardware.title') }}</span>
        </h2>
        <p class="police-solution__section-desc pixso-body-lg" data-text-reveal="inline">
          {{ t('PublicSecurityInspection.sections.hardware.description1') }}<br />
          {{ t('PublicSecurityInspection.sections.hardware.description2') }}
        </p>
        <PixsoButton class="police-solution__hardware-cta" :text="t('PublicSecurityInspection.sections.hardware.cta')" to="/about/contact" />
      </div>

      <HardwareCalloutScroller
        class="police-solution__hardware-callout"
        :image-src="hardwareDogImage"
        :image-alt="t('PublicSecurityInspection.alt.hardwareDog')"
        :aria-label="t('PublicSecurityInspection.alt.hardwareMap')"
        :items="hardwareItems"
        :image-layout="hardwareImageLayout"
        stage-aspect-ratio="1627 / 799"
        viewport-fit
        viewport-height="calc(100vh - var(--pixso-nav-height) - 32px)"
        viewport-inset="clamp(72px, 7vw, 160px)"
        :scroll-step="430"
        mobile-image-width="var(--police-hardware-mobile-image-width)"
        :scroll-hint-src="scrollHintIcon"
        scroll-hint-placement="viewport"
        scroll-hint-bottom="6vh"
      />
    </section>

    <section class="police-solution__section police-solution__phase-section">
      <div class="pixso-container police-solution__section-head">
        <p class="pixso-eyebrow">{{ t('PublicSecurityInspection.sections.phase.eyebrow') }}</p>
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.phase.title') }}</span>
        </h2>
        <p
          class="police-solution__section-desc police-solution__section-desc--wide pixso-body-lg"
          data-text-reveal="inline"
          v-html="phaseDescriptionHtml"
        ></p>
        <p
          class="police-solution__section-desc police-solution__section-desc--wide pixso-body-lg"
          data-text-reveal="inline"
          v-html="phaseDescription2Html"
        ></p>
      </div>

      <div class="pixso-container">
        <TabbedVideoPlayer
          :items="videoPhases"
          :poster="videoPosterImage"
          :caption="t('PublicSecurityInspection.sections.phase.videoNote')"
          :tablist-label="t('PublicSecurityInspection.sections.phase.tablistAria')"
        />
      </div>
    </section>

    <section class="police-solution__section police-solution__platform-section">
      <div class="pixso-container police-solution__section-head">
        <p class="pixso-eyebrow">{{ t('PublicSecurityInspection.sections.platform.eyebrow') }}</p>
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="police-solution__title-accent pixso-heading-xl">{{ t('PublicSecurityInspection.sections.platform.titleAccent') }}</span>
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.platform.title') }}</span>
        </h2>
        <p class="police-solution__section-desc police-solution__section-desc--wide pixso-body-lg police-solution__platform-desc" data-text-reveal="inline">
          {{ t('PublicSecurityInspection.sections.platform.descriptionPrefix') }}
          <span class="police-solution__desc-accent">{{ t('PublicSecurityInspection.sections.platform.descriptionHighlight') }}</span>
          {{ t('PublicSecurityInspection.sections.platform.descriptionSuffix') }}
        </p>
      </div>

      <div class="police-solution__wide police-solution__platform-shell">
        <PlatformGrid
          :items="platformCards"
          :aria-label="t('PublicSecurityInspection.sections.platform.title')"
        />
      </div>
    </section>

    <section class="police-solution__section police-solution__scenario-section police-solution__section--last">
      <div class="pixso-container police-solution__section-head">
        <h2 class="police-solution__split-title" data-text-reveal="inline">
          <span class="pixso-heading-xl">{{ t('PublicSecurityInspection.sections.scenario.title') }}</span>
        </h2>
      </div>

      <div class="police-solution__wide police-solution__scenario-grid">
        <ScenarioGrid
          :items="scenarioCards"
          :aria-label="t('PublicSecurityInspection.sections.scenario.title')"
          title-font-size="var(--pixso-fs-36)"
          title-line-height="var(--pixso-lh-36)"
          description-font-size="var(--pixso-fs-16)"
          description-line-height="var(--pixso-lh-16)"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PixsoButton from '@/components/pixso/PixsoButton.vue'
import AccordionImageGallery from '@/components/pixso/AccordionImageGallery.vue'
import PlatformGrid from '@/components/pixso/PlatformGrid.vue'
import ScenarioGrid from '@/components/pixso/ScenarioGrid.vue'
import HardwareCalloutScroller from '@/components/pixso/HardwareCalloutScroller.vue'
import TabbedVideoPlayer from '@/components/pixso/TabbedVideoPlayer.vue'
import FeatureMosaicGrid from '@/components/pixso/FeatureMosaicGrid.vue'
import { formatSmartTextHtml } from '@/utils/formatSmartTextHtml'

import heroVideo from '@/assets/videos/pixso-police-solution/public-security-banner.mp4'
import fastBootVideoZh from '@/assets/videos/pixso-police-solution/fast-boot.mp4'
import fastBootVideoEn from '@/assets/i18n/en/solution/police/fast-boot.mp4'
import quickDeployVideoZh from '@/assets/videos/pixso-police-solution/quick-deploy.mp4'
import quickDeployVideoEn from '@/assets/i18n/en/solution/police/quick-deploy.mp4'
import routePlanningVideoZh from '@/assets/videos/pixso-police-solution/route-planning.mp4'
import routePlanningVideoEn from '@/assets/i18n/en/solution/police/route-planning.mp4'
import obstacleAvoidanceVideoZh from '@/assets/videos/pixso-police-solution/obstacle-avoidance.mp4'
import obstacleAvoidanceVideoEn from '@/assets/i18n/en/solution/police/obstacle-avoidance.mp4'
import hazardRecognitionVideoZh from '@/assets/videos/pixso-police-solution/hazard-recognition.mp4'
import hazardRecognitionVideoEn from '@/assets/i18n/en/solution/police/hazard-recognition.mp4'
import nightWorkVideoZh from '@/assets/videos/pixso-police-solution/night-work.mp4'
import nightWorkVideoEn from '@/assets/i18n/en/solution/police/night-work.mp4'
import autoRechargeVideoZh from '@/assets/videos/pixso-police-solution/auto-recharge.mp4'
import autoRechargeVideoEn from '@/assets/i18n/en/solution/police/auto-recharge.mp4'
import painCityImage from '@/assets/images/pixso-police-solution/pain-coverage.jpg'
import painResponseImage from '@/assets/images/pixso-police-solution/pain-response-security-check.jpg'
import painCostImage from '@/assets/images/pixso-police-solution/pain-cost-police-motorcycle.jpg'
import painDecisionImage from '@/assets/images/pixso-police-solution/pain-decision.jpg'
import featureMainDogImage from '@/assets/images/pixso-police-solution/feature-public-main-clean.jpg'
import featureAiVisionImage from '@/assets/images/pixso-police-solution/feature-ai-risk-clean.jpg'
import featureShowcaseVoiceImage from '@/assets/images/pixso-police-solution/feature-voice-clean.jpg'
import featureSlamMapImage from '@/assets/images/pixso-police-solution/feature-slam-map.jpg'
import featureSlopeImage from '@/assets/images/pixso-police-solution/feature-slope-clean.jpg'
import featureObstacleImage from '@/assets/images/pixso-police-solution/feature-obstacle-clean.jpg'
import featureTemperatureImage from '@/assets/images/pixso-police-solution/feature-temperature-clean.jpg'
import featureSpeedImage from '@/assets/images/pixso-police-solution/feature-speed-clean.jpg'
import autonomousCruiseIcon from '@/assets/images/pixso-police-solution/feature-icons/autonomous-cruise.png'
import enduranceIcon from '@/assets/images/pixso-police-solution/feature-icons/endurance.png'
import chargeIcon from '@/assets/images/pixso-police-solution/feature-icons/charge.svg'
import hardwareDogImage from '@/assets/images/pixso-police-solution/hardware-go2-official.png'
import scrollHintIcon from '@/assets/images/pixso-common/xiahua.svg'
import videoPosterImage from '@/assets/images/pixso-police-solution/phase-obstacle-poster.webp'
import platformTaskImage from '@/assets/images/pixso-police-solution/platform-task.jpg'
import platformControlImage from '@/assets/images/pixso-police-solution/platform-control.jpg'
import platformAlarmImage from '@/assets/images/pixso-police-solution/platform-alarm.jpg'
import platformDataImage from '@/assets/images/pixso-police-solution/platform-data.jpg'
import platformAlgorithmImage from '@/assets/images/pixso-police-solution/platform-algorithm.jpg'
import platformDeviceImage from '@/assets/images/pixso-police-solution/platform-device.jpg'
import scenarioParkImage from '@/assets/images/pixso-police-solution/scenario-park.webp'
import scenarioBusinessImage from '@/assets/images/pixso-police-solution/scenario-business.webp'
import scenarioCommunityImage from '@/assets/images/pixso-police-solution/scenario-community.webp'
import scenarioStationImage from '@/assets/images/pixso-police-solution/scenario-station.webp'
import scenarioCampusImage from '@/assets/images/pixso-police-solution/scenario-campus.webp'
import scenarioGovernmentImage from '@/assets/images/pixso-police-solution/scenario-government.webp'

const { t, locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const painPoints = computed(() => [
  {
    initial: t('PublicSecurityInspection.painPoints.coverage.initial'),
    title: t('PublicSecurityInspection.painPoints.coverage.title'),
    summary: t('PublicSecurityInspection.painPoints.coverage.summary'),
    description: t('PublicSecurityInspection.painPoints.coverage.description')
  },
  {
    initial: t('PublicSecurityInspection.painPoints.response.initial'),
    title: t('PublicSecurityInspection.painPoints.response.title'),
    summary: t('PublicSecurityInspection.painPoints.response.summary'),
    description: t('PublicSecurityInspection.painPoints.response.description')
  },
  {
    initial: t('PublicSecurityInspection.painPoints.cost.initial'),
    title: t('PublicSecurityInspection.painPoints.cost.title'),
    summary: t('PublicSecurityInspection.painPoints.cost.summary'),
    description: t('PublicSecurityInspection.painPoints.cost.description')
  },
  {
    initial: t('PublicSecurityInspection.painPoints.decision.initial'),
    title: t('PublicSecurityInspection.painPoints.decision.title'),
    summary: t('PublicSecurityInspection.painPoints.decision.summary'),
    description: t('PublicSecurityInspection.painPoints.decision.description')
  }
])

const painGalleryItems = computed(() => [
  {
    key: 'coverage',
    initial: painPoints.value[0].initial,
    title: painPoints.value[0].title,
    summary: painPoints.value[0].summary,
    body: painPoints.value[0].description,
    image: painCityImage,
    imageAlt: t('PublicSecurityInspection.alt.painCity')
  },
  {
    key: 'response',
    initial: painPoints.value[1].initial,
    title: painPoints.value[1].title,
    summary: painPoints.value[1].summary,
    body: painPoints.value[1].description,
    image: painResponseImage,
    imageAlt: t('PublicSecurityInspection.alt.featureMainDog')
  },
  {
    key: 'cost',
    initial: painPoints.value[2].initial,
    title: painPoints.value[2].title,
    summary: painPoints.value[2].summary,
    body: painPoints.value[2].description,
    image: painCostImage,
    imageAlt: t('PublicSecurityInspection.alt.featureSideBuilding')
  },
  {
    key: 'decision',
    initial: painPoints.value[3].initial,
    title: painPoints.value[3].title,
    summary: painPoints.value[3].summary,
    body: painPoints.value[3].description,
    image: painDecisionImage,
    imageAlt: t('PublicSecurityInspection.alt.featureCompactDog')
  }
])

const featureMosaicItems = computed(() => [
  {
    key: 'main',
    variant: 'hero',
    group: 'hero',
    image: featureMainDogImage,
    imageAlt: t('PublicSecurityInspection.alt.featureShowcaseMain'),
    title: t('PublicSecurityInspection.sections.features.mainTitle'),
    tags: [
      t('PublicSecurityInspection.sections.features.mainTagsLine1'),
      t('PublicSecurityInspection.sections.features.mainTagsLine2')
    ],
    footnote: {
      value: t('PublicSecurityInspection.sections.features.size'),
      label: t('PublicSecurityInspection.sections.features.sizeLabel')
    }
  },
  {
    key: 'ai',
    variant: 'image',
    group: 'topWide',
    tone: 'ai',
    image: featureAiVisionImage,
    imageAlt: t('PublicSecurityInspection.alt.featureAi'),
    title: t('PublicSecurityInspection.features.ai.title'),
    description: t('PublicSecurityInspection.features.ai.description')
  },
  {
    key: 'patrol',
    variant: 'action',
    group: 'topWide',
    iconSrc: autonomousCruiseIcon,
    title: t('PublicSecurityInspection.features.patrol.title'),
    subtitle: t('PublicSecurityInspection.features.patrol.subtitle')
  },
  {
    key: 'voice',
    variant: 'image',
    group: 'topNarrow',
    tone: 'voice',
    image: featureShowcaseVoiceImage,
    imageAlt: t('PublicSecurityInspection.alt.featureVoice'),
    title: t('PublicSecurityInspection.features.voice.titleLine1'),
    subtitle: t('PublicSecurityInspection.features.voice.titleLine2')
  },
  {
    key: 'slam',
    variant: 'image',
    group: 'topNarrow',
    tone: 'slam',
    image: featureSlamMapImage,
    imageAlt: t('PublicSecurityInspection.alt.featureSlam'),
    title: t('PublicSecurityInspection.features.slam.title'),
    description: t('PublicSecurityInspection.features.slam.description')
  },
  {
    key: 'endurance',
    variant: 'metric',
    group: 'bottomLead',
    iconSrc: enduranceIcon,
    metric: {
      value: t('PublicSecurityInspection.metrics.endurance.value'),
      label: t('PublicSecurityInspection.metrics.endurance.title')
    }
  },
  {
    key: 'slope',
    variant: 'image',
    group: 'bottomLeftStack',
    tone: 'photo',
    image: featureSlopeImage,
    metric: {
      value: t('PublicSecurityInspection.metrics.slope.value'),
      label: t('PublicSecurityInspection.metrics.slope.title')
    }
  },
  {
    key: 'temperature',
    variant: 'image',
    group: 'bottomLeftStack',
    tone: 'photo',
    image: featureTemperatureImage,
    imagePosition: 'center',
    metric: {
      value: t('PublicSecurityInspection.metrics.temperature.value'),
      label: t('PublicSecurityInspection.metrics.temperature.title')
    }
  },
  {
    key: 'obstacle',
    variant: 'image',
    group: 'bottomRightStack',
    tone: 'photo',
    image: featureObstacleImage,
    metric: {
      value: t('PublicSecurityInspection.metrics.obstacle.value'),
      label: t('PublicSecurityInspection.metrics.obstacle.title')
    }
  },
  {
    key: 'speed',
    variant: 'image',
    group: 'bottomRightStack',
    tone: 'photo',
    image: featureSpeedImage,
    metric: {
      value: t('PublicSecurityInspection.metrics.speed.value'),
      label: t('PublicSecurityInspection.metrics.speed.title')
    }
  },
  {
    key: 'charge',
    variant: 'stats',
    group: 'bottomTrail',
    iconSrc: chargeIcon,
    metrics: [
      {
        value: t('PublicSecurityInspection.metrics.charge.value'),
        label: t('PublicSecurityInspection.metrics.charge.title')
      },
      {
        value: t('PublicSecurityInspection.metrics.continuous.value'),
        label: t('PublicSecurityInspection.metrics.continuous.title')
      }
    ]
  }
])

const hardwareImageLayout = {
  left: 1.5,
  top: 5,
  width: 98.6
}

const hardwareItems = computed(() => [
  {
    key: 'lidar',
    title: t('PublicSecurityInspection.hardwareItems.lidar.title'),
    note: { x: 0, y: 3.4, width: 26.5 },
    dot: { x: 43.7, y: 32.7 },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 43.7, y: 32.7 }
    },
    lines: [
      [{ text: t('PublicSecurityInspection.hardwareItems.lidar.line1') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.lidar.line2') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.lidar.line3'), highlight: true }]
    ]
  },
  {
    key: 'camera',
    title: t('PublicSecurityInspection.hardwareItems.camera.title'),
    side: 'right',
    note: { x: 73.5, y: 3.4, width: 26.5 },
    dot: { x: 50.7, y: 21.5 },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 50.7, y: 21.5 }
    },
    lines: [
      [{ text: t('PublicSecurityInspection.hardwareItems.camera.line1') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.camera.line2') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.camera.line3') }]
    ]
  },
  {
    key: 'stereo',
    title: t('PublicSecurityInspection.hardwareItems.stereo.title'),
    note: { x: 0, y: 38.3, width: 26.5 },
    dot: { x: 39.7, y: 43.1 },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 39.7, y: 43.1 }
    },
    lines: [
      [{ text: t('PublicSecurityInspection.hardwareItems.stereo.line1') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.stereo.line2') }],
      [{ text: t('PublicSecurityInspection.hardwareItems.stereo.line3'), highlight: true }]
    ]
  },
  {
    key: 'edge',
    title: t('PublicSecurityInspection.hardwareItems.edge.title'),
    side: 'right',
    note: { x: 73.5, y: 53.2, width: 26.5 },
    dot: { x: 54.2, y: 41.8 },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 54.2, y: 41.8 }
    },
    lines: [
      [{ text: t('PublicSecurityInspection.hardwareItems.edge.line1') }],
      [
        { text: t('PublicSecurityInspection.hardwareItems.edge.line2Prefix') },
        { text: t('PublicSecurityInspection.hardwareItems.edge.line2Highlight'), highlight: true },
        { text: t('PublicSecurityInspection.hardwareItems.edge.line2Suffix') }
      ],
      [
        { text: t('PublicSecurityInspection.hardwareItems.edge.line3Prefix') },
        { text: t('PublicSecurityInspection.hardwareItems.edge.line3Highlight'), highlight: true },
        { text: t('PublicSecurityInspection.hardwareItems.edge.line3Suffix') }
      ]
    ]
  }
])

const videoPhases = computed(() => [
  { key: 'fast-boot', buttonLabel: t('PublicSecurityInspection.phases.fastBoot.name'), subtitle: t('PublicSecurityInspection.phases.fastBoot.detail'), src: isEnglish.value ? fastBootVideoEn : fastBootVideoZh },
  { key: 'quick-deploy', buttonLabel: t('PublicSecurityInspection.phases.quickDeploy.name'), subtitle: t('PublicSecurityInspection.phases.quickDeploy.detail'), src: isEnglish.value ? quickDeployVideoEn : quickDeployVideoZh },
  { key: 'route-planning', buttonLabel: t('PublicSecurityInspection.phases.route.name'), subtitle: t('PublicSecurityInspection.phases.route.detail'), src: isEnglish.value ? routePlanningVideoEn : routePlanningVideoZh },
  { key: 'obstacle-avoidance', buttonLabel: t('PublicSecurityInspection.phases.avoid.name'), subtitle: t('PublicSecurityInspection.phases.avoid.detail'), src: isEnglish.value ? obstacleAvoidanceVideoEn : obstacleAvoidanceVideoZh },
  { key: 'hazard-recognition', buttonLabel: t('PublicSecurityInspection.phases.recognition.name'), subtitle: t('PublicSecurityInspection.phases.recognition.detail'), src: isEnglish.value ? hazardRecognitionVideoEn : hazardRecognitionVideoZh },
  { key: 'night-work', buttonLabel: t('PublicSecurityInspection.phases.night.name'), subtitle: t('PublicSecurityInspection.phases.night.detail'), src: isEnglish.value ? nightWorkVideoEn : nightWorkVideoZh },
  { key: 'auto-recharge', buttonLabel: t('PublicSecurityInspection.phases.recharge.name'), subtitle: t('PublicSecurityInspection.phases.recharge.detail'), src: isEnglish.value ? autoRechargeVideoEn : autoRechargeVideoZh }
])

const phaseDescriptionHtml = computed(() =>
  formatSmartTextHtml(
    t('PublicSecurityInspection.sections.phase.description'),
    ['?1', 'AI??'],
    'police-solution__nowrap'
  )
)

const phaseDescription2Html = computed(() =>
  formatSmartTextHtml(t('PublicSecurityInspection.sections.phase.description2'), [], 'police-solution__nowrap')
)

const platformCards = computed(() => [
  {
    key: 'task',
    image: platformTaskImage,
    alt: t('PublicSecurityInspection.alt.platformTask'),
    label: t('PublicSecurityInspection.platformModules.task')
  },
  {
    key: 'control',
    image: platformControlImage,
    alt: t('PublicSecurityInspection.alt.platformControl'),
    label: t('PublicSecurityInspection.platformModules.control')
  },
  {
    key: 'alarm',
    image: platformAlarmImage,
    alt: t('PublicSecurityInspection.alt.platformAlarm'),
    label: t('PublicSecurityInspection.platformModules.alarm')
  },
  {
    key: 'data',
    image: platformDataImage,
    alt: t('PublicSecurityInspection.alt.platformData'),
    label: t('PublicSecurityInspection.platformModules.data')
  },
  {
    key: 'algorithm',
    image: platformAlgorithmImage,
    alt: t('PublicSecurityInspection.alt.platformAlgorithm'),
    label: t('PublicSecurityInspection.platformModules.algorithm')
  },
  {
    key: 'device',
    image: platformDeviceImage,
    alt: t('PublicSecurityInspection.alt.platformDevice'),
    label: t('PublicSecurityInspection.platformModules.device')
  }
])

const scenarioCards = computed(() => [
  {
    key: 'park',
    title: t('PublicSecurityInspection.scenarios.park.title'),
    description: t('PublicSecurityInspection.scenarios.park.description'),
    image: scenarioParkImage,
    imageAlt: t('PublicSecurityInspection.scenarios.park.alt')
  },
  {
    key: 'business',
    title: t('PublicSecurityInspection.scenarios.business.title'),
    description: t('PublicSecurityInspection.scenarios.business.description'),
    image: scenarioBusinessImage,
    imageAlt: t('PublicSecurityInspection.scenarios.business.alt')
  },
  {
    key: 'community',
    title: t('PublicSecurityInspection.scenarios.community.title'),
    description: t('PublicSecurityInspection.scenarios.community.description'),
    image: scenarioCommunityImage,
    imageAlt: t('PublicSecurityInspection.scenarios.community.alt')
  },
  {
    key: 'station',
    title: t('PublicSecurityInspection.scenarios.station.title'),
    description: t('PublicSecurityInspection.scenarios.station.description'),
    image: scenarioStationImage,
    imageAlt: t('PublicSecurityInspection.scenarios.station.alt')
  },
  {
    key: 'campus',
    title: t('PublicSecurityInspection.scenarios.campus.title'),
    description: t('PublicSecurityInspection.scenarios.campus.description'),
    image: scenarioCampusImage,
    imageAlt: t('PublicSecurityInspection.scenarios.campus.alt')
  },
  {
    key: 'government',
    title: t('PublicSecurityInspection.scenarios.government.title'),
    description: t('PublicSecurityInspection.scenarios.government.description'),
    image: scenarioGovernmentImage,
    imageAlt: t('PublicSecurityInspection.scenarios.government.alt')
  }
])

</script>

<style scoped>
.police-solution {
  padding-bottom: var(--police-page-bottom-space);
  --police-color-bg: var(--pixso-color-bg);
  --police-color-surface: var(--pixso-color-surface);
  --police-color-text: var(--pixso-color-text);
  --police-color-inverse: var(--pixso-color-bg);
  --police-color-card-deep: var(--pixso-color-surface);
  --police-color-card-mid: var(--pixso-color-surface);
  --police-color-card-end: #282938;
  --police-color-card-grid: #171820;
  --police-color-card-dark: #131418;
  --police-color-text-subtle: rgba(255, 255, 255, 0.82);
  --police-feature-gap: var(--pixso-gap-md);
  --police-card-radius: var(--pixso-radius-lg);
  --police-card-radius-compact: var(--pixso-radius-md);
  --police-card-radius-soft: calc(var(--pixso-radius-lg) - var(--pixso-gap-xs));
  --police-mobile-card-padding: var(--pixso-fs-20);
  --police-section-space: var(--pixso-section-gap-xl);
  --police-section-head-max: 1200px;
  --police-pain-shell-max: 1200px;
  --police-pain-shell-top: var(--pixso-gap-lg);
  --police-how-offset-desktop: var(--pixso-section-gap-xl);
  --police-how-copy-top-desktop: 0px;
  --police-feature-bottom-height: 514px;
  --police-hardware-stage-width: 1627px;
  --police-hardware-stage-offset: 175px;
  --police-hardware-section-top: var(--pixso-section-gap-xl);
  --police-platform-section-top: var(--pixso-section-gap-xl);
  --police-scenario-section-top: var(--pixso-section-gap-xl);
  --police-hardware-stage-top: 140px;
  --police-phase-section-top: var(--pixso-section-gap-xl);
  --police-page-bottom-space: var(--pixso-section-gap-xl);
}

.police-solution__hero {
  position: relative;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  background: var(--police-color-bg);
  overflow: hidden;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
}

.police-solution__hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.24) 0%, rgba(0, 0, 0, 0.08) 52%, rgba(0, 0, 0, 0.48) 100%);
  pointer-events: none;
}

.police-solution__hero-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.police-solution__hero-copy {
  position: absolute;
  z-index: 2;
  top: clamp(96px, 12vw, 172px);
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100% - var(--pixso-page-padding)), 1920px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--police-color-text);
  pointer-events: none;
}

.police-solution__hero-title {
  margin: 0;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.police-solution__hero-subtitle {
  margin: 8px 0 0;
  font-size: var(--pixso-fs-64);
  font-weight: 700;
  line-height: var(--pixso-lh-64);
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.police-solution__section {
  padding-top: clamp(120px, 11vw, 180px);
}

.police-solution__section--last {
  padding-bottom: 0;
}

.police-solution__section-head {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--police-section-head-max));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-fs-16);
  text-align: center;
}

.police-solution__section-head--tight {
  padding-top: clamp(96px, 10vw, 140px);
}

.police-solution__section-head.police-solution__section-head--hardware {
  gap: 0;
}

.police-solution__section-head--hardware .police-solution__split-title,
.police-solution__section-head--hardware .police-solution__section-desc {
  margin-top: var(--pixso-fs-16);
}

.police-solution__hardware-cta {
  margin-top: var(--pixso-gap-lg);
}

.police-solution__split-title {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  align-items: center;
}

.police-solution__split-title > span,
.police-solution__how-title {
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-48), 5vw, var(--pixso-fs-96));
  line-height: clamp(var(--pixso-lh-48), 6vw, var(--pixso-lh-96));
}

.police-solution__title-accent {
  color: var(--pixso-color-primary);
}

.police-solution__section-desc {
  max-width: 760px;
  margin: 0;
  color: var(--pixso-color-muted);
}

.police-solution__section-desc :deep(.police-solution__nowrap) {
  display: inline-block;
  white-space: nowrap;
}

.police-solution__section-desc--wide {
  max-width: 1080px;
}


.police-solution__pain-shell {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--police-pain-shell-max));
  margin-top: var(--police-pain-shell-top);
}

.police-solution__how {
  position: relative;
  min-height: clamp(460px, 40vw, 680px);
  background: url("@/assets/images/pixso-police-solution/how-bg.jpg") center/cover no-repeat;
}

.police-solution__how::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.72) 18%, rgba(0, 0, 0, 0) 42%),
    linear-gradient(180deg, rgba(0, 0, 0, 0) 58%, rgba(0, 0, 0, 0.72) 82%, #000 100%);
  pointer-events: none;
}

.police-solution__how-stage {
  position: relative;
  z-index: 1;
  min-height: inherit;
}

.police-solution__wide-scroll {
  margin-top: clamp(48px, 4vw, 64px);
  overflow-x: auto;
  overflow-y: hidden;
}

.police-solution__wide-media {
  margin: 0;
}

.police-solution__wide-media img {
  width: 100%;
  display: block;
  border-radius: var(--police-card-radius-soft);
}

.police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="charge"] .feature-mosaic-card__stat-value) {
  font-size: var(--pixso-fs-56);
  line-height: var(--pixso-lh-56);
}

.police-solution__features-section .police-solution__split-title > span {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

@media (max-width: 1200px) {
  .police-solution {
    --police-page-bottom-space: clamp(120px, 10vw, 180px);
  }
}

@media (max-width: 1200px) {
  .police-solution__wide-media img {
    min-width: 920px;
  }

  .police-solution__hero-copy {
    top: 84px;
    width: min(calc(100% - 32px), 760px);
  }

  .police-solution__hero {
    min-height: var(--pixso-hero-min);
  }

  .police-solution__hero-video {
    min-height: var(--pixso-hero-min);
    object-fit: cover;
    object-position: center top;
  }
}

@media (max-width: 768px) {
  .police-solution__section {
    padding-top: 88px;
  }

  .police-solution__section-head {
    gap: var(--pixso-gap-sm);
  }

  .police-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .police-solution__hero-copy {
    top: 76px;
    width: calc(100% - 32px);
  }

  .police-solution__hero-subtitle {
    margin-top: 4px;
  }

  .police-solution__wide-media img {
    min-width: 760px;
  }
}

.police-solution__wide {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1792px);
  margin-inline: auto;
}

.police-solution__how {
  min-height: clamp(540px, 56.25vw, 1080px);
}

.police-solution__how-stage {
  display: flex;
  align-items: flex-start;
}

.police-solution__how-copy {
  padding-top: clamp(120px, 13vw, 220px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--pixso-color-text);
}

.police-solution__how-title {
  margin: 0;
}

.police-solution__how-subtitle {
  margin: 12px 0 0;
  font-size: var(--pixso-fs-36);
  line-height: var(--pixso-lh-36);
  font-weight: 700;
}

.police-solution__how-english {
  margin: 0;
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
  text-transform: uppercase;
}

.police-solution__how-tags {
  margin: 28px 0 0;
  font-size: var(--pixso-fs-28);
  line-height: var(--pixso-lh-28);
}

.police-solution__feature-showcase {
  margin-top: clamp(48px, 4vw, 64px);
}

.police-solution__hardware-callout {
  --police-hardware-mobile-image-width: min(100%, clamp(360px, 56vw, 680px));
  margin: clamp(56px, 6vh, 96px) auto 0;
}

.police-solution__desc-accent {
  color: var(--pixso-color-primary);
}

.police-solution__platform-shell {
  margin-top: clamp(48px, 4vw, 64px);
}

.police-solution__image-rows {
  margin-top: clamp(48px, 4vw, 64px);
  display: flex;
  flex-direction: column;
  gap: var(--police-feature-gap);
  overflow-x: auto;
  overflow-y: hidden;
}

.police-solution__image-rows img {
  width: 100%;
  display: block;
  flex: 0 0 auto;
}

.police-solution__scenario-grid {
  margin-top: clamp(48px, 4vw, 64px);
}

@media (min-width: 1201px) {
  .police-solution__hero-copy {
    top: 120px;
  }

  .police-solution__hero-subtitle {
    margin-top: 0;
  }

  .police-solution__section-head {
    gap: 12px;
  }

  .police-solution__split-title {
    gap: 0;
  }

  .police-solution__pain {
    padding-top: 96px;
  }

  .police-solution__how {
    margin-top: var(--police-how-offset-desktop);
    padding-top: 0;
    min-height: min(1080px, 56.25vw);
  }

  .police-solution__how-copy {
    padding-top: var(--police-how-copy-top-desktop);
  }

  .police-solution__features-section {
    padding-top: var(--police-section-space);
  }

  .police-solution__hardware-section {
    padding-top: var(--police-hardware-section-top);
  }

  .police-solution__phase-section {
    padding-top: var(--police-phase-section-top);
  }

  .police-solution__platform-section {
    padding-top: var(--police-platform-section-top);
  }

  .police-solution__scenario-section {
    padding-top: var(--police-scenario-section-top);
  }

  .police-solution__scenario-section .police-solution__section-head {
    --police-section-head-max: var(--pixso-page-max);
  }
}

@media (max-width: 1200px) {
  .police-solution__hardware-callout {
    width: min(calc(100% - var(--pixso-page-padding) * 2), var(--police-hardware-stage-width));
    --police-hardware-mobile-image-width: min(100%, clamp(520px, 56vw, 680px));
    margin-top: clamp(72px, 5vw, 96px);
  }
}

@media (max-width: 1200px) {
  .police-solution__image-rows img {
    width: 1120px;
    max-width: none;
  }

}

@media (max-width: 1200px) {
  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy) {
    top: 50%;
    left: 50%;
    right: auto;
    width: max-content;
    max-width: 82%;
    padding: 0;
    transform: translate(-50%, -50%);
    text-align: left;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-value) {
    white-space: nowrap;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy span) {
    margin-top: calc(var(--pixso-gap-xs) * 0.5);
    text-align: left;
  }
}

@media (max-width: 768px) {
  .police-solution__hardware-callout {
    --police-hardware-mobile-image-width: min(100%, clamp(300px, 74vw, 430px));
  }

  .police-solution__hardware-callout :deep(.hardware-callout-scroller__mobile-list) {
    margin-inline: var(--pixso-page-padding);
  }

  .police-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .police-solution__how {
    min-height: 520px;
  }

  .police-solution__how-copy {
    padding-top: 104px;
  }

  .police-solution__image-rows {
    gap: var(--pixso-gap-sm);
  }

  .police-solution__image-rows img {
    width: 920px;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__main-copy h3) {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__main-copy p) {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__size p) {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__size span) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card--ai .feature-mosaic-card__image-copy h3) {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__image-copy h3),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__text h3) {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__image-copy p),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__text p),
  .police-solution__feature-showcase :deep(.feature-mosaic-card--ai .feature-mosaic-card__image-copy p),
  .police-solution__feature-showcase :deep(.feature-mosaic-card--slam .feature-mosaic-card__image-copy p) {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy) {
    top: 50%;
    left: 50%;
    right: auto;
    width: max-content;
    max-width: 82%;
    padding: 0;
    transform: translate(-50%, -50%);
    text-align: left;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-value) {
    white-space: nowrap;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="temperature"] .feature-mosaic-card__photo-copy span) {
    margin-top: calc(var(--pixso-gap-xs) * 0.5);
    text-align: left;
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__metric-value),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__photo-value),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__stat-value) {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .police-solution__feature-showcase :deep(.feature-mosaic-card__metric-panel span),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__photo-copy span),
  .police-solution__feature-showcase :deep(.feature-mosaic-card__stat-block > span) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }
}

@media (max-width: 480px) {
  .police-solution--en .police-solution__feature-showcase {
    --police-ai-card-min-height: clamp(0px, calc(684.5px - 115vw), 236px);
  }

  .police-solution--en .police-solution__feature-showcase :deep(.feature-mosaic-grid__mobile-stack--top-left .feature-mosaic-card[data-feature-key="ai"]) {
    width: 100%;
    max-width: 100%;
    min-height: var(--police-ai-card-min-height);
  }

  .police-solution--en .police-solution__feature-showcase :deep(.feature-mosaic-grid__mobile-stack--top-right) {
    height: 100%;
    grid-template-rows: repeat(2, minmax(0, 1fr));
  }

  .police-solution--en .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="ai"] .feature-mosaic-card__image-copy h3) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }

  .police-solution--en .police-solution__feature-showcase :deep(.feature-mosaic-card[data-feature-key="ai"] .feature-mosaic-card__image-copy p) {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
  }
}

</style>
